/**
 * React Router configuration
 * Defines all routes matching the Next.js pages structure
 */
import { createBrowserRouter, Navigate } from 'react-router-dom';
import MyApp from './pages/_app';

// Lazy load page components for code splitting
import { lazy } from 'react';
import { PlatoUserLayout, type RouteHandle } from './features/plato-user-layout/components/plato-user-layout';
import { SuperAdminLayout } from './layouts/super-admin-layout';
import { SUPPORTED_LOCALES } from './lib/i18n/i18n';
import { LocaleRedirect } from './locale-redirect';

// ============================================================================
// AUTHENTICATION PAGES (public)
// ============================================================================
const SignInPage = lazy(() => import('./pages/sign-in/[[...index]]'));

// ============================================================================
// PUBLIC PAGES
// ============================================================================
const NotFoundPage = lazy(() => import('./pages/404'));

// ============================================================================
// PROTECTED PAGES (require authentication)
// ============================================================================
const HomePage = lazy(() => import('./pages/home/index'));
const CustomersListPage = lazy(() => import('./pages/customers/index'));
const CustomerDetailPage = lazy(() => import('./pages/customers/[customer_id]/index'));
const CustomerActivityPage = lazy(() => import('./pages/customers/[customer_id]/activity'));
const CustomerInsightsPage = lazy(() => import('./pages/customers/[customer_id]/insights'));
const CustomerInfoPage = lazy(() => import('./pages/customers/[customer_id]/info'));
const DraftsListPage = lazy(() => import('./pages/drafts/index'));
const DraftHeaderPage = lazy(() => import('./pages/drafts/[draft_id]/header'));
const DraftLineItemsPage = lazy(() => import('./pages/drafts/[draft_id]/line-items'));
const QuotesListPage = lazy(() => import('./pages/quotes/index'));
const QuoteDetailPage = lazy(() => import('./pages/quotes/[quote_id]/index'));
const OrdersListPage = lazy(() => import('./pages/orders/index'));
const OrderDetailPage = lazy(() => import('./pages/orders/[order_id]/index'));
const TasksPage = lazy(() => import('./pages/tasks/index'));
const EventsPage = lazy(() => import('./pages/events/index'));
const InventoryPage = lazy(() => import('./pages/inventory/[product_id]'));
const TapiIntegrationPage = lazy(() => import('./pages/integrations/tapi/[phone]'));
const ExplorerPage = lazy(() => import('./pages/explorer'));

// Admin pages (use SuperAdminLayout via nested routes)
const AdminDashboardPage = lazy(() => import('./pages/admin/index'));
const AdminUsersPage = lazy(() => import('./pages/admin/admin-users'));
const AdminCustomersListPage = lazy(() => import('./pages/admin/customers/index'));
const AdminCustomerDetailPage = lazy(() => import('./pages/admin/customers/[customer_id]/index'));
const AdminCustomerDetailsPage = lazy(() => import('./pages/admin/customers/[customer_id]/details'));
const AdminCustomerUserCountPage = lazy(() => import('./pages/admin/customers/[customer_id]/user-count'));

export const router = createBrowserRouter([
    ...SUPPORTED_LOCALES.flatMap((locale) =>
        [`/${locale}/*`, `/${locale}`].map((path) => ({
            path,
            element: <LocaleRedirect targetLocale={locale} />,
        })),
    ),
    // Main app routes
    {
        path: '/',
        element: <MyApp />,
        errorElement: <NotFoundPage />,
        children: [
            {
                index: true,
                element: <Navigate to="home" replace />,
            },

            // ================================================================
            // PUBLIC ROUTES
            // ================================================================
            {
                path: 'sign-in/*',
                element: <SignInPage />,
            },

            // ================================================================
            // PROTECTED ROUTES (wrapped in PlatoUserLayout)
            // ================================================================
            {
                element: <PlatoUserLayout />,
                children: [
                    {
                        path: 'home',
                        element: <HomePage />,
                    },
                    {
                        path: 'customers',
                        children: [
                            {
                                index: true,
                                element: <CustomersListPage />,
                            },
                            {
                                path: ':customer_id',
                                children: [
                                    {
                                        index: true,
                                        element: <CustomerDetailPage />,
                                    },
                                    {
                                        path: 'activity',
                                        element: <CustomerActivityPage />,
                                    },
                                    {
                                        path: 'insights',
                                        element: <CustomerInsightsPage />,
                                    },
                                    {
                                        path: 'info',
                                        element: <CustomerInfoPage />,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'quotes',
                        children: [
                            {
                                index: true,
                                element: <QuotesListPage />,
                            },
                            {
                                path: ':quote_id',
                                element: <QuoteDetailPage />,
                            },
                        ],
                    },
                    {
                        path: 'orders',
                        children: [
                            {
                                index: true,
                                element: <OrdersListPage />,
                            },
                            {
                                path: ':order_id',
                                element: <OrderDetailPage />,
                            },
                        ],
                    },
                    {
                        path: 'drafts',
                        children: [
                            {
                                index: true,
                                element: <DraftsListPage />,
                            },
                            {
                                path: ':draft_id',
                                children: [
                                    {
                                        path: 'line-items',
                                        element: <DraftLineItemsPage />,
                                        /**
                                         * We need to disable drawers because the line items page
                                         * has its own custom drawers and they conflict with the global ones
                                         * */
                                        handle: {
                                            disableDrawers: true,
                                        } satisfies RouteHandle,
                                    },
                                    {
                                        path: 'header',
                                        element: <DraftHeaderPage />,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: 'tasks',
                        element: <TasksPage />,
                    },
                    {
                        path: 'events',
                        element: <EventsPage />,
                    },
                    {
                        path: 'inventory/:product_id',
                        element: <InventoryPage />,
                    },
                    {
                        path: 'integrations/tapi/:phone',
                        element: <TapiIntegrationPage />,
                    },
                    {
                        path: 'explorer',
                        element: <ExplorerPage />,
                    },
                ],
            },

            // Admin routes (wrapped in SuperAdminLayout)
            {
                path: 'admin',
                element: <SuperAdminLayout />,
                children: [
                    {
                        index: true,
                        element: <AdminDashboardPage />,
                    },
                    {
                        path: 'admin-users',
                        element: <AdminUsersPage />,
                    },
                    {
                        path: 'customers',
                        children: [
                            {
                                index: true,
                                element: <AdminCustomersListPage />,
                            },
                            {
                                path: ':customer_id',
                                children: [
                                    {
                                        index: true,
                                        element: <AdminCustomerDetailPage />,
                                    },
                                    {
                                        path: 'details',
                                        element: <AdminCustomerDetailsPage />,
                                    },
                                    {
                                        path: 'user-count',
                                        element: <AdminCustomerUserCountPage />,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },

            // ================================================================
            // 404 - Catch all
            // ================================================================
            {
                path: '*',
                element: <NotFoundPage />,
            },
        ],
    },
]);
